//
// Copyright (c) 2025 Comcast
//

import { useTranslation } from 'react-i18next'
import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import * as sessionUserSelector from 'selectors/sessionUser'

import colors from 'components/utils/colors'

import MainWrap from 'components/Shared/Skeleton/MainWrap/Index'
import Content from 'components/Shared/Skeleton/Content/Index'
import Header from 'components/Shared/Header/Index'
import TicketSidebar from 'components/App/TicketProviderTicketsPage/shared/TicketSidebar'
import LoadingIndicator from 'components/App/TicketsSearchResultsPage/LoadingIndicator'
import PageSection from './PageSection'

const TicketsCount = styled.div`
  display: flex;
  align-items: center;
  height: 16px;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.secondary20};
`

const TicketProviderTicketsSearchResultsPage = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  let ticketProviders = useSelector(sessionUserSelector.ticketProviders)
  ticketProviders = ticketProviders.filter(provider => provider.configured)

  const [countTicketsMap, setCountTicketsMap] = useState({})
  const [selectedTicket, setSelectedTicket] = useState(null)

  const searchValue = searchParams.get('search') || ''

  useEffect(() => {
    setCountTicketsMap({})
    setSelectedTicket(null)
  }, [searchValue])

  const getBreadcrumbs = () => {
    const breadcrumbsList = [{
      text: t('common.breadcrumbTicketsSearchResults'),
    }]
    return breadcrumbsList
  }

  const onTicketsLoaded = (ticketProviderId, countTickets) => {
    setCountTicketsMap(prevState => {
      return { ...prevState, [ticketProviderId]: countTickets }
    })
  }

  const onTicketSelected = (ticket) => {
    setSelectedTicket(ticket)
  }

  const onSidebarClose = () => {
    setSelectedTicket(null)
  }

  // get sum of countTicketsMap values
  const getTotalCountTickets = () => {
    return Object.values(countTicketsMap).reduce((acc, item) => acc + item, 0)
  }

  const isLoadingTickets = () => {
    const loadedTicketProviders = Object.values(countTicketsMap).length
    return (loadedTicketProviders < ticketProviders.length)
  }

  const totalTicketsCount = getTotalCountTickets()
  const totalTicketsCountText = t('ticketProviderTicketsSearchResultsPage.ticketsFound', { ticketsCount: totalTicketsCount })

  return (
    <MainWrap pageTitle={t('ticketProviderTicketsSearchResultsPage.pageHeader')}>
      <TicketSidebar
        ticket={selectedTicket}
        onClose={onSidebarClose}
        searchValue={searchValue}
      />
      <Content>
        <Header
          title={t('ticketProviderTicketsSearchResultsPage.pageHeader')}
          breadcrumbsList={getBreadcrumbs()}
        />
        <TicketsCount>
          {totalTicketsCountText}
          {isLoadingTickets() && <LoadingIndicator />}
        </TicketsCount>

        {ticketProviders.map(ticketProvider => {
          return (
            <PageSection
              key={ticketProvider.id}
              ticketProvider={ticketProvider}
              onTicketsLoaded={onTicketsLoaded}
              onTicketSelected={onTicketSelected}
              selectedTicketId={selectedTicket?.id}
            />
          )
        })}
      </Content>
    </MainWrap>
  )
}

export default TicketProviderTicketsSearchResultsPage
